import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { finalPrice } from "./format";

const STORAGE_KEY = "roz.booking.v1";
const BookingContext = createContext(null);

const readStored = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const key = (id, staffId) => `${id}::${staffId ?? "any"}`;

function reducer(items, action) {
  switch (action.type) {
    case "add": {
      const { service, staffId = null } = action;
      const k = key(service.id, staffId);
      if (items.some((i) => i.key === k)) return items;

      return [
        ...items,
        {
          key: k,
          id: service.id,
          slug: service.slug,
          name: service.name,
          category: service.category,
          art: service.art,
          photo: service.photo,
          tone: service.tone,
          tone2: service.tone2,
          duration: service.duration,
          staffId,
          price: finalPrice(service),
        },
      ];
    }

    case "staff":
      return items.map((i) =>
        i.key === action.key
          ? { ...i, staffId: action.staffId, key: key(i.id, action.staffId) }
          : i
      );

    case "remove":
      return items.filter((i) => i.key !== action.key);

    case "clear":
      return [];

    default:
      return items;
  }
}

export function BookingProvider({ children }) {
  const [items, dispatch] = useReducer(reducer, undefined, readStored);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* storage unavailable — basket stays in memory for this session */
    }
  }, [items]);

  // Lock the page behind the drawer without letting the scrollbar's
  // disappearance shift the layout underneath.
  useEffect(() => {
    if (!open) return undefined;

    const { body } = document;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    const prev = { overflow: body.style.overflow, pad: body.style.paddingInlineEnd };

    body.style.overflow = "hidden";
    if (gap > 0) body.style.paddingInlineEnd = `${gap}px`;

    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);

    return () => {
      body.style.overflow = prev.overflow;
      body.style.paddingInlineEnd = prev.pad;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const value = useMemo(() => {
    const total = items.reduce((n, i) => n + i.price, 0);
    const minutes = items.reduce((n, i) => n + i.duration, 0);

    return {
      items,
      count: items.length,
      total,
      minutes,
      open,
      setOpen,
      has: (id) => items.some((i) => i.id === id),
      add: (service, staffId) => {
        dispatch({ type: "add", service, staffId });
        setOpen(true);
      },
      setStaff: (k, staffId) => dispatch({ type: "staff", key: k, staffId }),
      remove: (k) => dispatch({ type: "remove", key: k }),
      clear: () => dispatch({ type: "clear" }),
    };
  }, [items, open]);

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

export const useBooking = () => {
  const ctx = useContext(BookingContext);
  if (!ctx) throw new Error("useBooking must be used inside <BookingProvider>");
  return ctx;
};
